"use client"

import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { Label } from "@/components/ui/label"
import { Switch } from "@/components/ui/switch"
import { Loader2, Pencil, TestTube, Unlink } from "lucide-react"

import type { DeliveryIntegrationView } from "@/server-actions/delivery-integrations"
import { useTranslations } from "next-intl"


type PendingAction = "test" | "toggle" | "disconnect" | null

export function DeliveryIntegrationItem({
  integration,
  pendingAction,
  onEdit,
  onTest,
  onToggle,
  onDisconnect,
}: {
  integration: DeliveryIntegrationView
  pendingAction: PendingAction
  onEdit: (integration: DeliveryIntegrationView) => void
  onTest: (integrationId: string) => void
  onToggle: (integrationId: string, active: boolean) => void
  onDisconnect: (integrationId: string) => void
}) {
  const tUi = useTranslations("Deliveries.DeliveriesDeliveryIntegrationItem")

  const busy = pendingAction !== null
  return (
    <div className="flex flex-wrap items-center justify-between gap-4 rounded-md border bg-background p-4">
      <div className="min-w-[200px] flex-1 space-y-1">
        <div className="flex flex-wrap items-center gap-2">
          <span className="font-medium">{integration.name}</span>
          <VerificationBadge integration={integration} />
          <PaymentCapabilityBadge integration={integration} />
        </div>
        <p className="text-xs text-muted-foreground">
          <span><span key={tUi("novaPostSender")}>{tUi("novaPostSender")}</span></span> {integration.config.sender.name}
        </p>
        {integration.lastTestErrorCode ? (
          <p className="text-xs text-destructive">
            <span><span key={tUi("lastTestFailed")}>{tUi("lastTestFailed")}</span></span> {formatErrorCode(integration.lastTestErrorCode)}
          </p>
        ) : null}
      </div>
      <div className="ml-auto flex flex-wrap items-center gap-2">
        <Button
          type="button"
          size="sm"
          variant="outline"
          onClick={() => onTest(integration.id)}
          disabled={busy}
        >
          {pendingAction === "test"
            ? <Loader2 className="mr-1 h-4 w-4 animate-spin" />
            : <TestTube className="mr-1 h-4 w-4" />}
          <span><span key={tUi("test")}>{tUi("test")}</span></span>
        </Button>
        <Button
          type="button"
          size="sm"
          variant="outline"
          onClick={() => onEdit(integration)}
          disabled={busy}
        >
          <Pencil className="mr-1 h-4 w-4" />
          <span><span key={tUi("edit")}>{tUi("edit")}</span></span>
        </Button>
        <div className="flex items-center gap-2 px-1">
          <Label htmlFor={`delivery-toggle-${integration.id}`} className="text-xs">
            <span key={integration.isActive ? tUi("enabled") : tUi("disabled")}>{integration.isActive ? tUi("enabled") : tUi("disabled")}</span>
          </Label>
          <Switch
            id={`delivery-toggle-${integration.id}`}
            checked={integration.isActive}
            onCheckedChange={active => onToggle(integration.id, active)}
            disabled={busy || integration.verificationStatus !== "succeeded"}
          />
        </div>
        <Button
          type="button"
          size="sm"
          variant="outline"
          onClick={() => onDisconnect(integration.id)}
          disabled={busy}
        >
          {pendingAction === "disconnect"
            ? <Loader2 className="mr-1 h-4 w-4 animate-spin" />
            : <Unlink className="mr-1 h-4 w-4" />}
          <span><span key={tUi("disconnect")}>{tUi("disconnect")}</span></span>
        </Button>
      </div>
    </div>
  )
}

function VerificationBadge({ integration }: { integration: DeliveryIntegrationView }) {
  const tUi = useTranslations("Deliveries.DeliveriesDeliveryIntegrationItem")

  if (integration.verificationStatus === "pending") {
    return <Badge variant="secondary"><span key={tUi("verificationPending")}>{tUi("verificationPending")}</span></Badge>
  }
  if (integration.verificationStatus === "failed") {
    return <Badge variant="destructive"><span key={tUi("verificationFailed")}>{tUi("verificationFailed")}</span></Badge>
  }
  if (!integration.isActive) {
    return <Badge variant="secondary"><span key={tUi("disabled")}>{tUi("disabled")}</span></Badge>
  }
  return <Badge className="bg-green-100 text-green-800"><span key={tUi("connected")}>{tUi("connected")}</span></Badge>
}

function PaymentCapabilityBadge({
  integration,
}: {
  integration: DeliveryIntegrationView
}) {
  const tUi = useTranslations("Deliveries.DeliveriesDeliveryIntegrationItem")

  if (integration.supportsCashOnDelivery) {
    return (
      <Badge className="bg-green-100 text-green-800">
        <span key={tUi("paymentOnDelivery")}>{tUi("paymentOnDelivery")}</span></Badge>
    )
  }
  return <Badge variant="secondary"><span key={tUi("prepaymentOnly")}>{tUi("prepaymentOnly")}</span></Badge>
}

function formatErrorCode(code: string): string {
  return code.replaceAll("_", " ")
}
